import { lazy } from "react";
import App from "../App";
import { Outlet } from "react-router";
import OrdersList from "../components/Consultation/OrdersList";
import InitiateOrder from "../components/Consultation/InitiateOrder";
import OrderDetails from "../components/Consultation/OrderDetails";

const GuestOnlyRouter = lazy(() => import("./GuestOnlyRouter"));
const NoMatch = lazy(() => import("../components/Common/NoMatch"));
const AuthenticatedComponentsWrapper = lazy(() =>
  import("../shared/AuthenticatedComponentsWrapper")
);
const SelectCompany = lazy(() => import("../components/Home/SelectCompany"));
const MainPage = lazy(() => import("../components/Landing/main"));
const AboutPage = lazy(() => import("../components/Landing/about"));
const FaqPage = lazy(() => import("../components/Landing/faq"));
const ContactPage = lazy(() => import("../components/Landing/contact"));
const PrivacyPolicy = lazy(() => import("../components/Landing/privacy"));
const TermsCondition = lazy(() =>
  import("../components/Landing/termscondition")
);
const ChooseCompany = lazy(() => import("../components/Auth/ChooseCompany"));
const GovernmentLogin = lazy(() =>
  import("../components/Auth/GovernmentCompany/Login")
);
const RegistrationInstructions = lazy(() =>
  import("../components/Auth/GovernmentCompany/Instructions")
);
const GovernmentRegistration = lazy(() =>
  import("../components/Auth/GovernmentCompany/Register")
);
const RegistrationSuccess = lazy(() =>
  import("../components/Auth/GovernmentCompany/RegistrationSuccess")
);
const ResetPassword = lazy(() =>
  import("../components/Auth/GovernmentCompany/ResetPassword")
);
const CallbackHandler = lazy(() =>
  import("../components/Auth/CallbackHandler")
);
const VerifyEmail = lazy(() => import("../components/Auth/VerifyEmail"));
const GovVerifyEmail = lazy(() =>
  import("../components/Auth/GovernmentCompany/VerifyEmail")
);
const GovernmentOtp = lazy(() =>
  import("../components/Auth/GovernmentCompany/Otp")
);
const Certificate = lazy(() => import("../components/Certificate"));
const Entity = lazy(() => import("../components/Entity"));
const Home = lazy(() => import("../components/Home"));
const WorkshopPage = lazy(() => import("../components/Workshop"));
const SelectCard = lazy(() => import("../components/Payment/SelectCard"));
const TransferDetails = lazy(() =>
  import("../components/Payment/TransferDetails")
);
const Status = lazy(() =>
  import("../components/Payment/TransferDetails/Status")
);
const Review = lazy(() => import("../components/Payment/Review"));
const SadadDetails = lazy(() => import("../components/Payment/SadadDetails"));
const GenerateSadadInvoice = lazy(() =>
  import("../components/Payment/SadadDetails/GenerateSadadInvoice")
);
const CardDetails = lazy(() => import("../components/Payment/CardDetails"));
const Processing = lazy(() => import("../components/Payment/Processing"));
const PaymentSuccessful = lazy(() =>
  import("../components/Payment/PaymentSuccessful")
);
const PaymentFailed = lazy(() => import("../components/Payment/PaymentFailed"));
const Update = lazy(() => import("../components/Entity/Update"));
const RegistrationSuccessful = lazy(() =>
  import("../components/Workshop/RegistrationSuccessful")
);
const Orders = lazy(() => import("../components/Certificate/Orders"));
const Instructions = lazy(() =>
  import("../components/Certificate/Instructions")
);
const Grade = lazy(() => import("../components/Certificate/Grade"));
const Auditing = lazy(() => import("../components/Certificate/Auditing"));
const Rejected = lazy(() =>
  import("../components/Certificate/Result/Rejected")
);
const Accepted = lazy(() =>
  import("../components/Certificate/Result/Accepted")
);
const TrainingOrders = lazy(() =>
  import("../components/Training/TrainingOrders")
);
const Goals = lazy(() => import("../components/Training/Goals"));
const UpdateJob = lazy(() => import("../components/Jobs/UpdateJob"));
const ViewJob = lazy(() => import("../components/Jobs/ViewJob"));
const CreateJob = lazy(() => import("../components/Jobs/CreateJob"));
const JobsPage = lazy(() => import("../components/Jobs"));
const VerifyJobEmail = lazy(() => import("../components/Jobs/VerifyEmail"));
const ConsultationPage = lazy(() => import("../components/Consultation"));

// can be access by both logged in and guest users
const mainLayout = [
  {
    key: "main-page",
    path: "home",
    element: <MainPage />,
  },
  {
    key: "about",
    path: "about",
    element: <AboutPage />,
  },
  {
    key: "faq",
    path: "faq",
    element: <FaqPage />,
  },
  {
    key: "contact",
    path: "contact-us",
    element: <ContactPage />,
  },
  {
    key: "privacy",
    path: "privacy-policy",
    element: <PrivacyPolicy />,
  },
  {
    key: "terms",
    path: "terms-condition",
    element: <TermsCondition />,
  },
  {
    key: "no-match",
    path: "*",
    element: <NoMatch />,
  },
];

// can be accessed by guest users only
const publicLayout = [
  {
    key: "auth",
    path: "auth/*",
    element: (
      <GuestOnlyRouter>
        <Outlet />
      </GuestOnlyRouter>
    ),
    children: [
      {
        path: "",
        key: "choose-company",
        element: <ChooseCompany />,
      },
      {
        path: "callback",
        key: "callback",
        element: <CallbackHandler />,
      },
      {
        path: "verify-email",
        key: "verify-email",
        element: <VerifyEmail />,
      },
      {
        path: "government/*",
        key: "government",
        element: <Outlet />,
        children: [
          {
            path: "login",
            key: "government-login",
            element: <GovernmentLogin />,
          },
          {
            path: "instruction",
            key: "registration-instructions",
            element: <RegistrationInstructions />,
          },
          {
            path: "register",
            key: "government-registration",
            element: <GovernmentRegistration />,
          },
          {
            path: "verify-email-register",
            key: "gov-verify-email-register",
            element: <GovVerifyEmail />,
          },
          {
            path: "register/success",
            key: "registration-success",
            element: <RegistrationSuccess />,
          },
          {
            path: "otp",
            key: "otp",
            element: <GovernmentOtp />,
          },
          {
            path: "reset-password",
            key: "reset-password",
            element: <ResetPassword />,
          },
        ],
      },
    ],
  },
];

// can be accessed by logged in users only
const privateLayout = [
  {
    key: "company",
    path: ":companyId",
    element: (
      <AuthenticatedComponentsWrapper>
        <Outlet />
      </AuthenticatedComponentsWrapper>
    ),
    children: [
      {
        path: "",
        key: "company-home",
        element: <Home />,
      },
      {
        key: "support",
        path: "support",
        element: <ContactPage />,
      },
      {
        key: "payment",
        path: "payment/:uuid/*",
        element: <Outlet />,
        children: [
          {
            path: "review",
            key: "review",
            element: <Review />,
          },
          {
            path: "method",
            key: "method/*",
            element: <Outlet />,
            children: [
              {
                path: "",
                key: "select-card",
                element: <SelectCard />,
              },
              {
                path: "transfer",
                key: "transfer",
                element: <TransferDetails />,
              },
              {
                path: "transfer/status",
                key: "status",
                element: <Status />,
              },
              {
                path: "sadad",
                key: "sadad",
                element: <SadadDetails />,
              },
              {
                path: "sadad/invoice",
                key: "invoice",
                element: <GenerateSadadInvoice />,
              },
              {
                path: ":paymentMethod/*",
                key: "paymentMethod",
                element: <Outlet />,
                children: [
                  {
                    path: "details",
                    key: "details",
                    element: <CardDetails />,
                  },
                  {
                    path: ":checkoutId/details/processing",
                    key: "Processing",
                    element: <Processing />,
                  },
                  {
                    path: "successful",
                    key: "pay-success",
                    element: <PaymentSuccessful />,
                  },
                  {
                    path: "failed",
                    key: "pay-failed",
                    element: <PaymentFailed />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: "certificates",
        path: "certificate/*",
        element: <Outlet />,
        children: [
          {
            path: "",
            key: "Orders",
            element: <Orders />,
          },
          {
            path: "instructions",
            key: "instructions",
            element: <Instructions />,
          },
          {
            path: ":uuid/*",
            key: "certificate",
            element: <Outlet />,
            children: [
              {
                path: "",
                key: "certificate-home",
                element: <Certificate />,
              },
              {
                path: "grade",
                key: "grade",
                element: <Grade />,
              },
              {
                path: "evidence",
                key: "evidence",
                element: <Certificate />,
              },
              {
                path: "auditing",
                key: "auditing",
                element: <Auditing />,
              },
              {
                path: "rejected",
                key: "rejected",
                element: <Rejected />,
              },
              {
                path: "accepted",
                key: "accepted",
                element: <Accepted />,
              },
            ],
          },
        ],
      },
      {
        key: "entities",
        path: "entity/*",
        element: <Outlet />,
        children: [
          {
            key: "entity",
            path: "",
            element: <Entity />,
          },
          {
            key: "entity-update",
            path: "update",
            element: <Update />,
          },
        ],
      },
      {
        key: "training",
        path: "training/*",
        element: <Outlet />,
        children: [
          {
            key: "training-orders",
            path: "",
            element: <TrainingOrders />,
          },
          {
            key: "training-goals",
            path: "goals",
            element: <Goals />,
          },
        ],
      },
      {
        key: "workshop",
        path: "workshop/*",
        element: <Outlet />,
        children: [
          {
            path: "",
            key: "workshop-list",
            element: <WorkshopPage />,
          },
          {
            key: "workshop-registration",
            path: "successful",
            element: <RegistrationSuccessful />,
          },
        ],
      },
      {
        key: "jobs",
        path: "jobs/*",
        element: <Outlet />,
        children: [
          {
            path: "",
            key: "jobs-list",
            element: <JobsPage />,
          },
          {
            path: "create",
            key: "create-job",
            element: <CreateJob />,
          },
          {
            path: "view/:jobId",
            key: "view-job",
            element: <ViewJob />,
          },
          {
            path: "update/:jobId",
            key: "update-job",
            element: <UpdateJob />,
          },
          {
            path: "verify-email/:jobId",
            key: "verify-job-email",
            element: <VerifyJobEmail />,
          },
        ],
      },
      {
        key: "consultation",
        path: "consultation/*",
        element: <Outlet />,
        children: [
          {
            path: "",
            key: "consultation-home",
            element: <ConsultationPage />,
          },
          {
            path: "initiate-order",
            key: "order-consultation",
            element: <InitiateOrder />,
          },
          {
            path: "order-list",
            key: "order-list",
            element: <OrdersList />,
          },
          {
            path: ":uuid/order-details",
            key: "order-details",
            element: <OrderDetails />,
          },
        ],
      },
    ],
  },
  {
    key: "select-company",
    // Set to empty string so it becomes the default when no subpath is provided
    path: "",
    element: (
      <AuthenticatedComponentsWrapper>
        <SelectCompany />
      </AuthenticatedComponentsWrapper>
    ),
  },
];

export const ROUTES = [
  {
    key: "app",
    path: "/",
    element: <App />,
    children: [...privateLayout, ...publicLayout, ...mainLayout],
  },
];
